import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, uploadApi } from "../../lib/api";

const initialForm = {
  title: "",
  category: "General",
  description: "",
  audience: "all",
  status: "published",
  file: null,
};

export default function AdminDownloadForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [existingFile, setExistingFile] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api(`/api/admin/downloads/${id}`)
      .then((item) => {
        setForm({
          title: item.title || "",
          category: item.category || "General",
          description: item.description || "",
          audience: item.audience || "all",
          status: item.status || "published",
          file: null,
        });
        setExistingFile(item.file_url || "");
      })
      .catch((err) => setError(err.message));
  }, [id]);

  function updateField(e) {
    const { name, value, files } = e.target;
    setForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSaving(true);

    if (!id && !form.file) {
      setSaving(false);
      setError("Please select a file to upload.");
      return;
    }

    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("category", form.category);
    fd.append("description", form.description || "");
    fd.append("audience", form.audience);
    fd.append("status", form.status);
    if (form.file) fd.append("file", form.file);

    try {
      await uploadApi(id ? `/api/admin/downloads/${id}` : "/api/admin/downloads", fd, id ? "PUT" : "POST");
      navigate("/adxm/downloads");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout
      title={id ? "Edit Download" : "Add Download"}
      subtitle="Upload official PDFs, forms, calendars, timetables, guides and other school resources."
      actions={<Link to="/adxm/downloads" className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-bold text-slate-700">Back to Downloads</Link>}
    >
      <form onSubmit={submit} className="mx-auto max-w-4xl rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        {error && <div className="mb-5 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

        <div className="grid gap-5 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Title</span>
            <input name="title" value={form.title} onChange={updateField} required className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
          </label>
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Category</span>
            <input name="category" value={form.category} onChange={updateField} required className="mt-2 w-full rounded-xl border border-slate-300 p-3" placeholder="Academic Calendar, Forms, Timetable..." />
          </label>
        </div>

        <label className="mt-5 block">
          <span className="text-sm font-bold text-slate-700">Description</span>
          <textarea name="description" value={form.description} onChange={updateField} rows="5" className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
        </label>

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Audience</span>
            <select name="audience" value={form.audience} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 p-3">
              <option value="all">All</option>
              <option value="students">Students</option>
              <option value="staff">Staff</option>
              <option value="applicants">Applicants</option>
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-bold text-slate-700">Status</span>
            <select name="status" value={form.status} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 p-3">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </label>
        </div>

        <label className="mt-5 block">
          <span className="text-sm font-bold text-slate-700">File {id ? "(leave empty to keep existing)" : ""}</span>
          <input name="file" type="file" onChange={updateField} accept=".pdf,.jpg,.jpeg,.png,.webp" className="mt-2 w-full rounded-xl border border-slate-300 p-3" />
        </label>

        {existingFile && (
          <p className="mt-3 text-sm text-slate-600">
            Current file: <a href={existingFile} target="_blank" rel="noopener noreferrer" className="font-bold text-emerald-700">Open uploaded file</a>
          </p>
        )}

        <button disabled={saving} className="mt-8 rounded-xl bg-emerald-700 px-6 py-3 font-bold text-white disabled:opacity-60">
          {saving ? "Saving..." : "Save Download"}
        </button>
      </form>
    </AdminLayout>
  );
}
